import { EmptyState, TableSkeleton } from "@/components/accounting/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import {
	useGetSchema,
	useListSchemas,
	useStoreSchema,
} from "@/hooks/use-accounting";
import { formatDate } from "@/lib/format";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowLeft, ChevronRight, Code, FileText, Loader2, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/accounting/schemas")({
	component: SchemasPage,
});

type ChartAccountRow = {
	code?: string;
	name?: string;
	type?: string;
	normalBalance?: string;
};

type EntryTypeRow = {
	key?: string;
	name?: string;
	debit?: string;
	credit?: string;
};

function SchemasPage() {
	const [selectedKey, setSelectedKey] = useState<string | null>(null);

	if (selectedKey) {
		return (
			<SchemaDetail
				schemaKey={selectedKey}
				onBack={() => setSelectedKey(null)}
			/>
		);
	}

	return <SchemaList onSelect={setSelectedKey} />;
}

// ---------------------------------------------------------------------------
// Schema List
// ---------------------------------------------------------------------------

function SchemaList({ onSelect }: { onSelect: (key: string) => void }) {
	const { t } = useTranslation("accounting");
	const { data, isLoading } = useListSchemas();
	const [createOpen, setCreateOpen] = useState(false);
	const storeSchema = useStoreSchema();
	const [searchText, setSearchText] = useState("");

	const schemas = data?.schemas ?? [];
	const filteredSchemas = useMemo(
		() =>
			schemas.filter(
				(s) =>
					s.name.toLowerCase().includes(searchText.toLowerCase()) ||
					s.key.toLowerCase().includes(searchText.toLowerCase()),
			),
		[schemas, searchText],
	);

	useBreadcrumbs([{ label: t("nav_schemas") }]);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: `${schemas.length} ${t("schemas_count", { defaultValue: "схем" })}`,
				align: "left" as const,
			},
		],
		[schemas.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						const json = JSON.stringify(schemas, null, 2);
						const blob = new Blob([json], {
							type: "application/json",
						});
						const url = URL.createObjectURL(blob);
						const a = document.createElement("a");
						a.href = url;
						a.download = "schemas.json";
						a.click();
						URL.revokeObjectURL(url);
					}}
				>
					{t("toolbar_export")}
				</Button>
				<Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("schema_create")}
				</Button>
			</div>
			<div className="flex-1 overflow-auto p-1 space-y-4">
			{isLoading ? (
				<TableSkeleton rows={5} cols={4} />
			) : filteredSchemas.length === 0 ? (
				<EmptyState message={t("no_schemas")} />
			) : (
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{filteredSchemas.map((schema) => (
						<Card
							key={schema.key}
							className="cursor-pointer hover:bg-accent/30 transition-colors group"
							onClick={() => onSelect(schema.key)}
						>
							<CardContent className="p-4">
								<div className="flex items-start justify-between">
									<div className="min-w-0 flex-1">
										<p className="text-sm font-medium truncate">
											{schema.name}
										</p>
										<p className="text-xs text-muted-foreground font-mono mt-0.5">
											{schema.key}
										</p>
									</div>
									<Badge variant="secondary" className="ml-2 shrink-0">
										v{schema.version}
									</Badge>
								</div>
								<div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
									<span>
										{schema.createdAt
											? formatDate(schema.createdAt.toDate())
											: "\u2014"}
									</span>
									<ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			)}

			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<CreateSchemaDialog
					onSubmit={(values) => {
						storeSchema.mutate(values, {
							onSuccess: () => {
								setCreateOpen(false);
								toast.success(
									t("schema_created", {
										defaultValue: "Схема создана",
									}),
								);
							},
						});
					}}
					isPending={storeSchema.isPending}
				/>
			</Dialog>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Schema Detail
// ---------------------------------------------------------------------------

function SchemaDetail({
	schemaKey,
	onBack,
}: {
	schemaKey: string;
	onBack: () => void;
}) {
	const { t } = useTranslation("accounting");
	const { data: schema, isLoading } = useGetSchema(schemaKey);
	const storeSchema = useStoreSchema();

	const [activeSection, setActiveSection] = useState<"chart" | "types">(
		"chart",
	);
	const [editing, setEditing] = useState(false);
	const [editJson, setEditJson] = useState("");
	const [jsonError, setJsonError] = useState<string | null>(null);

	useBreadcrumbs([
		{ label: t("nav_schemas"), href: "/accounting/schemas" },
		{ label: schema?.name ?? schemaKey },
	]);

	const chartAccounts = useMemo(() => {
		if (!schema?.chartJson) return [];
		try {
			const parsed = JSON.parse(schema.chartJson);
			return (
				Array.isArray(parsed) ? parsed : (parsed.accounts ?? [])
			) as ChartAccountRow[];
		} catch {
			return [];
		}
	}, [schema?.chartJson]);

	const entryTypes = useMemo(() => {
		if (!schema?.entryTypesJson) return [];
		try {
			const parsed = JSON.parse(schema.entryTypesJson);
			return (Array.isArray(parsed) ? parsed : []) as EntryTypeRow[];
		} catch {
			return [];
		}
	}, [schema?.entryTypesJson]);
	const chartAccountCols = useMemo<ColumnDef<ChartAccountRow>[]>(
		() => [
			{
				accessorKey: "code",
				header: t("account_code"),
				cell: ({ row }) => (
					<span className="font-mono text-xs">{row.original.code ?? "—"}</span>
				),
				size: 96,
			},
			{
				accessorKey: "name",
				header: t("account_name"),
				cell: ({ row }) => {
					const depth = (row.original.code?.split(".").length ?? 1) - 1;
					return (
						<span
							className="block"
							style={{ paddingLeft: `${12 + depth * 16}px` }}
						>
							{row.original.name ?? "—"}
						</span>
					);
				},
			},
			{
				accessorKey: "type",
				header: t("account_type"),
				cell: ({ row }) =>
					row.original.type ? (
						<Badge variant="outline" className="text-[10px]">
							{row.original.type}
						</Badge>
					) : (
						"—"
					),
				size: 112,
			},
			{
				accessorKey: "normalBalance",
				header: t("normal_balance_short", {
					defaultValue: "Сальдо",
				}),
				cell: ({ row }) => (
					<span className="text-xs text-muted-foreground">
						{row.original.normalBalance ?? "—"}
					</span>
				),
				size: 96,
			},
		],
		[t],
	);
	const entryTypeCols = useMemo<ColumnDef<EntryTypeRow>[]>(
		() => [
			{
				accessorKey: "key",
				header: t("entry_type_key"),
				cell: ({ row }) => (
					<span className="font-mono text-xs">{row.original.key ?? "—"}</span>
				),
				size: 128,
			},
			{
				accessorKey: "name",
				header: t("account_name"),
				cell: ({ row }) => row.original.name ?? "—",
			},
			{
				accessorKey: "debit",
				header: t("line_debit"),
				cell: ({ row }) => (
					<span className="font-mono text-xs">{row.original.debit ?? "—"}</span>
				),
				size: 96,
			},
			{
				accessorKey: "credit",
				header: t("line_credit"),
				cell: ({ row }) => (
					<span className="font-mono text-xs">
						{row.original.credit ?? "—"}
					</span>
				),
				size: 96,
			},
		],
		[t],
	);

	if (isLoading) {
		return (
			<div className="space-y-4 p-1">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	if (!schema) {
		return (
			<div className="space-y-4 p-1">
				<Button
					type="button"
					variant="ghost"
					className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
					onClick={onBack}
				>
					<ArrowLeft className="h-4 w-4" />
					{t("back")}
				</Button>
				<p className="text-muted-foreground">{t("no_schemas")}</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="flex items-center gap-3 px-2 py-2 border-b shrink-0">
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="rounded-md p-1 text-muted-foreground hover:bg-accent"
					onClick={onBack}
				>
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<h1 className="text-sm font-semibold">{schema.name}</h1>
				<Badge variant="secondary" className="text-[10px]">
					v{schema.version}
				</Badge>
				<span className="text-[10px] text-muted-foreground font-mono">
					{schema.key}
				</span>
				<div className="flex-1" />
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						if (editing) {
							setEditing(false);
							setJsonError(null);
						} else {
							const raw =
								activeSection === "chart"
									? schema?.chartJson
									: schema?.entryTypesJson;
							try {
								setEditJson(JSON.stringify(JSON.parse(raw || "[]"), null, 2));
							} catch {
								setEditJson(raw ?? "[]");
							}
							setEditing(true);
							setJsonError(null);
						}
					}}
				>
					{editing ? t("cancel", { ns: "common" }) : t("schema_edit")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						const raw =
							activeSection === "chart"
								? schema?.chartJson
								: schema?.entryTypesJson;
						navigator.clipboard.writeText(raw ?? "[]");
						toast.success(
							t("copied", { ns: "common", defaultValue: "Скопировано" }),
						);
					}}
				>
					{t("copy_json", { defaultValue: "Копировать JSON" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						if (!schema) return;
						const obj = {
							key: schema.key,
							name: schema.name,
							chart: JSON.parse(schema.chartJson || "[]"),
							entryTypes: JSON.parse(schema.entryTypesJson || "[]"),
						};
						const blob = new Blob([JSON.stringify(obj, null, 2)], {
							type: "application/json",
						});
						const url = URL.createObjectURL(blob);
						const a = document.createElement("a");
						a.href = url;
						a.download = `${schema.key}.json`;
						a.click();
						URL.revokeObjectURL(url);
					}}
				>
					{t("toolbar_export")}
				</Button>
				{editing && (
					<Button
						size="sm"
						className="h-9"
						onClick={() => {
							try {
								JSON.parse(editJson);
							} catch {
								setJsonError(
									t("schema_json_error", {
										defaultValue: "Невалидный JSON",
									}),
								);
								return;
							}
							const payload = {
								key: schema!.key,
								name: schema!.name,
								chartJson:
									activeSection === "chart" ? editJson : schema!.chartJson,
								entryTypesJson:
									activeSection === "types"
										? editJson
										: schema!.entryTypesJson,
							};
							storeSchema.mutate(payload, {
								onSuccess: () => {
									setEditing(false);
									toast.success(
										t("schema_saved", {
											defaultValue: "Схема сохранена",
										}),
									);
								},
							});
						}}
						disabled={storeSchema.isPending || !!jsonError}
					>
						{t("schema_save")}
					</Button>
				)}
			</div>

			{/* Section tabs */}
			<div className="flex items-center border-b px-2 shrink-0">
				<Button
					type="button"
					variant="ghost"
					onClick={() => {
						setActiveSection("chart");
						setEditing(false);
					}}
					className={`px-3 py-1.5 text-xs border-b-2 transition-colors ${
						activeSection === "chart"
							? "border-primary text-foreground font-medium"
							: "border-transparent text-muted-foreground hover:text-foreground"
					}`}
				>
					<FileText className="inline h-3.5 w-3.5 mr-1" />
					{t("chart_template")}
					{chartAccounts.length > 0 && (
						<span className="ml-1 text-[10px] text-muted-foreground">
							({chartAccounts.length})
						</span>
					)}
				</Button>
				<Button
					type="button"
					variant="ghost"
					onClick={() => {
						setActiveSection("types");
						setEditing(false);
					}}
					className={`px-3 py-1.5 text-xs border-b-2 transition-colors ${
						activeSection === "types"
							? "border-primary text-foreground font-medium"
							: "border-transparent text-muted-foreground hover:text-foreground"
					}`}
				>
					<Code className="inline h-3.5 w-3.5 mr-1" />
					{t("entry_types")}
					{entryTypes.length > 0 && (
						<span className="ml-1 text-[10px] text-muted-foreground">
							({entryTypes.length})
						</span>
					)}
				</Button>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-auto p-2">
				{editing ? (
					<div className="space-y-2">
						<Textarea
							className="font-mono text-xs min-h-[400px] resize-none"
							value={editJson}
							onChange={(e) => {
								setEditJson(e.target.value);
								setJsonError(null);
							}}
						/>
						{jsonError && (
							<p className="text-xs text-destructive">{jsonError}</p>
						)}
					</div>
				) : activeSection === "chart" ? (
					chartAccounts.length === 0 ? (
						<EmptyState message={t("no_chart_accounts")} />
					) : (
						<DataTable dense columns={chartAccountCols} data={chartAccounts} />
					)
				) : entryTypes.length === 0 ? (
					<EmptyState message={t("no_entry_types")} />
				) : (
					<DataTable dense columns={entryTypeCols} data={entryTypes} />
				)}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Create Schema Dialog
// ---------------------------------------------------------------------------

function CreateSchemaDialog({
	onSubmit,
	isPending,
}: {
	onSubmit: (values: {
		key: string;
		name: string;
		chartJson: string;
		entryTypesJson: string;
	}) => void;
	isPending: boolean;
}) {
	const { t } = useTranslation("accounting");
	const [key, setKey] = useState("");
	const [name, setName] = useState("");
	const [chartJson, setChartJson] = useState("[]");
	const [entryTypesJson, setEntryTypesJson] = useState("[]");

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		onSubmit({ key, name, chartJson, entryTypesJson });
	}

	return (
		<DialogContent className="max-w-lg">
			<DialogHeader>
				<DialogTitle>{t("schema_create")}</DialogTitle>
			</DialogHeader>
			<form onSubmit={handleSubmit} className="space-y-4">
				<div className="grid grid-cols-2 gap-3">
					<div className="space-y-1.5">
						<Label className="text-xs">{t("schema_key")}</Label>
						<Input
							value={key}
							onChange={(e) => setKey(e.target.value)}
							placeholder="my-schema"
							className="font-mono text-xs"
							required
						/>
					</div>
					<div className="space-y-1.5">
						<Label className="text-xs">{t("schema_name")}</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t("schema_name_placeholder", {
								defaultValue: "Основная схема",
							})}
							required
						/>
					</div>
				</div>
				<div className="space-y-1.5">
					<Label className="text-xs">{t("chart_template")} (JSON)</Label>
					<Textarea
						className="font-mono text-xs min-h-[80px]"
						value={chartJson}
						onChange={(e) => setChartJson(e.target.value)}
					/>
				</div>
				<div className="space-y-1.5">
					<Label className="text-xs">{t("entry_types")} (JSON)</Label>
					<Textarea
						className="font-mono text-xs min-h-[80px]"
						value={entryTypesJson}
						onChange={(e) => setEntryTypesJson(e.target.value)}
					/>
				</div>
				<DialogFooter>
					<Button type="submit" disabled={isPending} size="sm">
						{isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{t("schema_create")}
					</Button>
				</DialogFooter>
			</form>
		</DialogContent>
	);
}
